<template>
  <div class="transaction-detail">
    <div v-if="loading" class="loading">Loading...</div>

    <div v-else-if="transaction" class="content">
      <div class="header">
        <div class="header-top">
          <button @click="goBack" class="btn-back">← Back</button>
          <div v-if="isReviewMode" class="review-badge">Review Mode</div>
        </div>
        <h1>{{ transaction.order_number ? `Order #${transaction.order_number}` : 'Order (Pending)' }}</h1>
      </div>

      <div class="transaction-info">
        <div class="info-grid">
          <div class="info-item">
            <label>Order Date:</label>
            <span>{{ displayDate }}</span>
          </div>
          <div class="info-item">
            <label>Total Amount:</label>
            <span class="amount">{{ formatAmount(transaction.order_total) }}</span>
          </div>
          <div class="info-item">
            <label>Status:</label>
            <span :class="['status-badge', `status-${transaction.processing_status}`]">
              {{ transaction.processing_status }}
            </span>
          </div>
          <div class="info-item">
            <label>Monarch Match:</label>
            <span :class="['status-badge', `match-${transaction.match_status}`]">
              {{ transaction.match_status }}
            </span>
          </div>
          <div class="info-item" v-if="transaction.owner_name">
            <label>From:</label>
            <span class="owner-name">{{ transaction.owner_name }}</span>
          </div>
        </div>
      </div>

      <div class="items-section">
        <h2>Items ({{ transaction.items?.length || 0 }})</h2>

        <div v-if="!transaction.items || transaction.items.length === 0" class="empty-state">
          <p>No items found. Try re-syncing this transaction.</p>
        </div>

        <div v-else class="items-list">
          <div v-for="item in transaction.items" :key="item.id" class="item-card">
            <div class="item-header">
              <h3>{{ item.name }}</h3>
              <div class="item-header-right">
                <span class="item-amount">{{ formatItemAmount(item.amount, item.quantity) }}</span>
                <button
                  v-if="!isSyncedOrConfirmed"
                  class="btn-delete-item"
                  @click="handleDeleteItem(item)"
                  title="Delete item"
                >x</button>
              </div>
            </div>

            <p v-if="item.description" class="item-description">{{ item.description }}</p>

            <div class="item-details">
              <span>Quantity: {{ item.quantity || 1 }}</span>
              <span class="editable-amount" v-if="!isSyncedOrConfirmed">
                Price: $<input
                  type="number"
                  :value="item.amount"
                  step="0.01"
                  min="0"
                  class="amount-input"
                  @change="updateItemAmount(item, $event)"
                />
              </span>
              <span v-else>Price: {{ formatAmount(item.amount) }}</span>
            </div>

            <div class="category-section">
              <div class="suggested-category" v-if="item.suggested_category_name">
                <span class="label">AI Suggestion:</span>
                <span class="category-name">{{ item.suggested_category_name }}</span>
                <span v-if="item.ai_confidence" class="confidence">
                  ({{ (item.ai_confidence * 100).toFixed(0) }}% confident)
                </span>
              </div>

              <div class="category-selector">
                <label>Category:</label>
                <select
                  v-model="item.category_id"
                  @change="updateItemCategory(item)"
                  class="category-select"
                >
                  <option :value="null">Select category...</option>
                  <option
                    v-for="category in categories"
                    :key="category.id"
                    :value="category.id"
                  >
                    {{ category.name }}
                  </option>
                </select>
              </div>

              <div v-if="item.user_approved" class="approval-status">
                ✓ {{ item.user_modified ? 'Modified by you' : 'Approved' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Monarch Sync Summary - shows after transaction is synced -->
      <div v-if="isSynced" class="sync-summary">
        <h2>Monarch Sync Summary</h2>
        <div v-if="loadingMonarchSplits" class="sync-loading">
          Loading categories from Monarch...
        </div>
        <template v-else-if="categorySummary.length > 0">
          <p class="sync-summary-description">
            This receipt was {{ categorySummary.length === 1 ? 'categorized as' : 'split into ' + categorySummary.length + ' categories in' }} Monarch:
          </p>
          <div class="category-breakdown">
            <div v-for="(cat, idx) in categorySummary" :key="idx" class="category-row">
              <span class="category-name">{{ cat.name }}</span>
              <span class="category-amount">{{ formatAmount(cat.amount) }}</span>
            </div>
            <div v-if="categorySummary.length > 1" class="category-row category-total">
              <span class="category-name">Total</span>
              <span class="category-amount">{{ formatAmount(categorySummaryTotal) }}</span>
            </div>
          </div>
        </template>
        <p v-else class="sync-summary-description">
          Synced to Monarch. Category details not available.
        </p>
      </div>

      <div class="actions">
        <!-- $0 order: offer dismiss instead of sync -->
        <div v-if="isZeroOrder && !isDismissed && !isSyncedOrConfirmed" class="zero-order-notice">
          <p>This order was $0.00 (fully covered by credit or gift card). No Monarch sync needed.</p>
          <button
            @click="dismissTransaction"
            :disabled="dismissing"
            class="btn-dismiss"
          >
            {{ dismissing ? 'Dismissing...' : 'Dismiss Transaction' }}
          </button>
        </div>

        <!-- Normal flow: accept/confirm/sync buttons -->
        <div v-else-if="!isSyncedOrConfirmed" class="action-buttons">
          <button
            v-if="hasAnySuggestion"
            @click="acceptSuggestions"
            :disabled="accepting || !transaction.monarch_transaction_id"
            class="btn-accept"
          >
            {{ accepting ? 'Syncing...' : 'Accept AI Suggestions & Sync' }}
          </button>

          <button
            @click="confirmTransaction"
            :disabled="!canConfirm || confirming"
            class="btn-confirm"
          >
            {{ confirming ? 'Syncing...' : 'Confirm & Sync to Monarch' }}
          </button>

          <button
            @click="confirmDismissTransaction"
            :disabled="dismissing"
            class="btn-dismiss-transaction"
          >
            {{ dismissing ? 'Dismissing...' : 'Dismiss Receipt' }}
          </button>
        </div>

        <div v-if="isDismissed" class="dismissed-notice">
          <p>This transaction has been dismissed.</p>
          <button
            @click="restoreTransaction"
            :disabled="restoring"
            class="btn-restore"
          >
            {{ restoring ? 'Restoring...' : 'Restore Transaction' }}
          </button>
        </div>

        <button
          v-if="isSyncedOrConfirmed && !isDismissed"
          @click="resetTransaction"
          :disabled="resetting"
          class="btn-reset"
        >
          {{ resetting ? 'Resetting...' : 'Reset & Re-categorize' }}
        </button>

        <!-- Re-parse button: always available unless dismissed -->
        <button
          v-if="!isDismissed"
          @click="reparseTransaction"
          :disabled="reparsing"
          class="btn-reparse"
        >
          {{ reparsing ? 'Re-parsing...' : 'Re-parse with AI' }}
        </button>

        <p v-if="!allItemsCategorized && !isSyncedOrConfirmed && !isZeroOrder" class="warning">
          All items must be categorized before confirming
        </p>
        <p v-if="!transaction.monarch_transaction_id && !isSyncedOrConfirmed && !isZeroOrder" class="warning">
          No Monarch transaction matched. Please match manually.
        </p>
      </div>

      <!-- Debug Section -->
      <div class="debug-section">
        <button @click="showDebug = !showDebug" class="btn-toggle-debug">
          {{ showDebug ? '▼ Hide Debug Info' : '► Show Debug Info' }}
        </button>

        <div v-if="showDebug" class="debug-content">
          <h4 class="debug-section-title">Parsed Data</h4>
          <div class="debug-row">
            <label>Order Number:</label>
            <span>{{ transaction.order_number || '(not parsed)' }}</span>
          </div>
          <div class="debug-row">
            <label>Order Total:</label>
            <span>{{ transaction.order_total ? `$${transaction.order_total}` : '(not parsed)' }}</span>
          </div>
          <div class="debug-row">
            <label>Order Date (Gemini):</label>
            <span>{{ transaction.order_date || '(not parsed)' }}</span>
          </div>
          <div class="debug-row">
            <label>Email Date (from body):</label>
            <span>{{ transaction.email_date || '(not parsed)' }}</span>
          </div>
          <div class="debug-row">
            <label>Forward Date (envelope):</label>
            <span>{{ transaction.forward_date || '(not captured)' }}</span>
          </div>
          <div class="debug-row">
            <label>Parse Status:</label>
            <span>{{ transaction.parse_status }}</span>
          </div>
          <div v-if="transaction.parse_error" class="debug-row error">
            <label>Parse Error:</label>
            <span class="error-text">{{ transaction.parse_error }}</span>
          </div>

          <h4 class="debug-section-title">Raw Email</h4>
          <div class="debug-row">
            <label>Email Subject:</label>
            <span>{{ transaction.raw_email_subject || '(none)' }}</span>
          </div>
          <div class="debug-row">
            <label>Email Body Length:</label>
            <span>{{ transaction.raw_email_body_length || 0 }} characters</span>
          </div>

          <div v-if="!rawEmailLoaded" class="debug-row">
            <button @click="loadRawEmail" :disabled="loadingRawEmail" class="btn-load-email">
              {{ loadingRawEmail ? 'Loading...' : 'Load Email Body' }}
            </button>
          </div>

          <div v-if="rawEmailBody" class="debug-email-body">
            <div class="debug-tabs">
              <button
                :class="['debug-tab', { active: emailViewMode === 'cleaned' }]"
                @click="emailViewMode = 'cleaned'"
              >Cleaned ({{ cleanedEmailStats.cleaned_length.toLocaleString() }} chars)</button>
              <button
                :class="['debug-tab', { active: emailViewMode === 'raw' }]"
                @click="emailViewMode = 'raw'"
              >Raw HTML ({{ cleanedEmailStats.original_length.toLocaleString() }} chars)</button>
            </div>
            <p class="cleaning-info" v-if="cleanedEmailStats.original_length > 0">
              Cleaned with ftfy + html2text: {{ Math.round((1 - cleanedEmailStats.cleaned_length / cleanedEmailStats.original_length) * 100) }}% reduction
            </p>
            <pre class="email-preview">{{ emailViewMode === 'cleaned' ? cleanedEmailBody : rawEmailBody }}</pre>
            <button
              @click="debugReparse"
              :disabled="reparsing"
              class="btn-debug-reparse"
            >
              {{ reparsing ? 'Re-parsing...' : 'Re-parse Email' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="error-state">
      <p>Transaction not found</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTransactionsStore } from '../store/transactions'
import { useCategoriesStore } from '../store/categories'
import { transactionsAPI, syncAPI } from '../services/api'

const router = useRouter()
const route = useRoute()
const transactionsStore = useTransactionsStore()
const categoriesStore = useCategoriesStore()

const loading = ref(true)
const confirming = ref(false)
const resetting = ref(false)
const accepting = ref(false)
const reparsing = ref(false)
const dismissing = ref(false)
const restoring = ref(false)

// Monarch splits state (fetched from Monarch for synced transactions)
const monarchSplits = ref([])
const loadingMonarchSplits = ref(false)

// Debug state
const showDebug = ref(false)
const rawEmailBody = ref('')
const cleanedEmailBody = ref('')
const cleanedEmailStats = ref({ original_length: 0, cleaned_length: 0 })
const rawEmailLoaded = ref(false)
const loadingRawEmail = ref(false)
const emailViewMode = ref('cleaned')

const transaction = computed(() => transactionsStore.currentTransaction)
const allCategories = computed(() => categoriesStore.categories)
const budgetedOnly = ref(localStorage.getItem('budgetedCategoriesOnly') === 'true')

// Review mode: if ?review=true query param is present
const isReviewMode = computed(() => route.query.review === 'true')

// Watch for route param changes to reload transaction data
// This is needed because Vue Router reuses the component when navigating between transactions
watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      loading.value = true
      // Reset state when switching transactions
      rawEmailBody.value = ''
      rawEmailLoaded.value = false
      monarchSplits.value = []
      try {
        await transactionsStore.fetchTransaction(parseInt(newId))
      } catch (error) {
        console.error('Error loading transaction:', error)
      } finally {
        loading.value = false
      }
    }
  }
)

// Watch for transaction changes to fetch Monarch splits for synced transactions
watch(
  transaction,
  async (newTxn) => {
    if (newTxn?.processing_status === 'synced' && newTxn?.monarch_transaction_id) {
      await fetchMonarchSplits()
    } else {
      monarchSplits.value = []
    }
  },
  { immediate: true }
)

const categories = computed(() => {
  if (budgetedOnly.value) {
    return allCategories.value.filter((c) => c.planned_amount && c.planned_amount > 0)
  }
  return allCategories.value
})

const allItemsCategorized = computed(() => {
  if (!transaction.value?.items) return false
  return transaction.value.items.every((item) => item.category_id)
})

const isSyncedOrConfirmed = computed(() => {
  const status = transaction.value?.processing_status
  return status === 'synced' || status === 'confirmed' || status === 'dismissed' || transaction.value?.user_confirmed
})

const isSynced = computed(() => {
  return transaction.value?.processing_status === 'synced'
})

const categorySummary = computed(() => {
  // If we have Monarch splits, use those (actual data from Monarch)
  if (monarchSplits.value.length > 0) {
    return monarchSplits.value
      .map(s => ({
        name: s.category_name || 'Unknown',
        amount: Math.abs(s.amount || 0)
      }))
      .sort((a, b) => b.amount - a.amount)
  }

  // Fall back to deriving from local items
  if (!transaction.value?.items) return []

  // Group items by category and sum amounts
  const categoryMap = {}
  for (const item of transaction.value.items) {
    const catName = item.category_name || item.suggested_category_name || 'Uncategorized'
    const amount = (item.amount || 0) * (item.quantity || 1)
    if (!categoryMap[catName]) {
      categoryMap[catName] = 0
    }
    categoryMap[catName] += amount
  }

  // Convert to array and sort by amount descending
  return Object.entries(categoryMap)
    .map(([name, amount]) => ({ name, amount }))
    .sort((a, b) => b.amount - a.amount)
})

const categorySummaryTotal = computed(() => {
  return categorySummary.value.reduce((sum, cat) => sum + cat.amount, 0)
})

const fetchMonarchSplits = async () => {
  if (!transaction.value?.monarch_transaction_id || !isSynced.value) {
    monarchSplits.value = []
    return
  }

  try {
    loadingMonarchSplits.value = true
    const response = await syncAPI.getMonarchTransactionSplits(transaction.value.monarch_transaction_id)
    monarchSplits.value = response.data.splits || []
  } catch (error) {
    console.error('Error fetching Monarch splits:', error)
    monarchSplits.value = []
  } finally {
    loadingMonarchSplits.value = false
  }
}

const isZeroOrder = computed(() => {
  return transaction.value?.order_total === 0 || transaction.value?.order_total === '0' || transaction.value?.order_total === 0.0
})

const isDismissed = computed(() => {
  return transaction.value?.processing_status === 'dismissed'
})

const canConfirm = computed(() => {
  return (
    allItemsCategorized.value &&
    transaction.value?.monarch_transaction_id &&
    !isSyncedOrConfirmed.value
  )
})

const hasAnySuggestion = computed(() => {
  if (!transaction.value?.items) return false
  return transaction.value.items.some((item) => item.suggested_category_id)
})

// Cascade: order_date -> email_date -> forward_date
const displayDate = computed(() => {
  const t = transaction.value
  if (!t) return 'Date unknown'
  const dateStr = t.order_date || t.email_date || t.forward_date
  return dateStr ? formatDate(dateStr) : 'Date unknown'
})

onMounted(async () => {
  try {
    const transactionId = parseInt(route.params.id)

    await Promise.all([
      transactionsStore.fetchTransaction(transactionId),
      categoriesStore.fetchCategories()
    ])
  } catch (error) {
    console.error('Error loading transaction:', error)
  } finally {
    loading.value = false
  }
})

const updateItemCategory = async (item) => {
  try {
    await transactionsStore.updateItem(transaction.value.id, item.id, {
      category_id: item.category_id
    })
  } catch (error) {
    alert('Failed to update category: ' + (error.response?.data?.error || error.message))
  }
}

const updateItemAmount = async (item, event) => {
  const newAmount = parseFloat(event.target.value)
  if (isNaN(newAmount) || newAmount < 0) {
    event.target.value = item.amount
    return
  }
  try {
    await transactionsStore.updateItem(transaction.value.id, item.id, {
      amount: newAmount
    })
  } catch (error) {
    alert('Failed to update amount: ' + (error.response?.data?.error || error.message))
    event.target.value = item.amount
  }
}

const handleDeleteItem = async (item) => {
  if (!confirm(`Delete "${item.name}" from this transaction?`)) return
  try {
    await transactionsStore.deleteItem(transaction.value.id, item.id)
  } catch (error) {
    alert('Failed to delete item: ' + (error.response?.data?.error || error.message))
  }
}

const navigateAfterSync = async (confirmedTransactionId) => {
  // In review mode, go to the next transaction
  if (isReviewMode.value) {
    try {
      // Pass the ID of the transaction we just confirmed so backend can find the next one
      const response = await transactionsAPI.getNextReview(confirmedTransactionId)
      if (response.data.next && response.data.next.id !== confirmedTransactionId) {
        // Use replace to avoid back-button issues, then push the new route
        await router.replace({ path: `/transactions/${response.data.next.id}`, query: { review: 'true' } })
      } else {
        // No more transactions to review
        router.push('/review')
      }
    } catch (error) {
      console.error('Error getting next transaction:', error)
      router.push('/review')
    }
  } else {
    router.push('/transactions')
  }
}

const acceptSuggestions = async () => {
  // Capture ID before any async operations that might change state
  const currentId = transaction.value.id
  try {
    accepting.value = true
    await transactionsStore.acceptSuggestions(currentId)
    await navigateAfterSync(currentId)
  } catch (error) {
    alert('Failed to accept suggestions: ' + (error.response?.data?.error || error.message))
  } finally {
    accepting.value = false
  }
}

const confirmTransaction = async () => {
  if (!canConfirm.value) return

  // Capture ID before any async operations that might change state
  const currentId = transaction.value.id
  try {
    confirming.value = true
    await transactionsStore.confirmTransaction(currentId)
    await navigateAfterSync(currentId)
  } catch (error) {
    alert('Failed to confirm transaction: ' + (error.response?.data?.error || error.message))
  } finally {
    confirming.value = false
  }
}

const resetTransaction = async () => {
  if (!confirm('Reset this transaction? You can then re-categorize items and sync again.')) return

  try {
    resetting.value = true
    await transactionsStore.resetTransaction(transaction.value.id)
  } catch (error) {
    alert('Failed to reset transaction: ' + (error.response?.data?.error || error.message))
  } finally {
    resetting.value = false
  }
}

const reparseTransaction = async () => {
  if (!confirm('Re-parse this transaction with the latest AI prompts? Existing items will be replaced.')) return

  try {
    reparsing.value = true
    const result = await transactionsStore.reparseTransaction(transaction.value.id)
    alert(result.message || 'Transaction re-parsed successfully.')
  } catch (error) {
    alert('Failed to re-parse: ' + (error.response?.data?.error || error.message))
  } finally {
    reparsing.value = false
  }
}

const dismissTransaction = async () => {
  try {
    dismissing.value = true
    await transactionsStore.dismissTransaction(transaction.value.id)
  } catch (error) {
    alert('Failed to dismiss: ' + (error.response?.data?.error || error.message))
  } finally {
    dismissing.value = false
  }
}

const confirmDismissTransaction = async () => {
  if (!confirm('Dismiss this receipt? It will be removed from your main transaction list and won\'t be synced to Monarch.')) return
  await dismissTransaction()
}

const restoreTransaction = async () => {
  try {
    restoring.value = true
    await transactionsAPI.restore(transaction.value.id)
    // Reload the transaction to get updated status
    await transactionsStore.fetchTransaction(transaction.value.id)
  } catch (error) {
    alert('Failed to restore: ' + (error.response?.data?.error || error.message))
  } finally {
    restoring.value = false
  }
}

const goBack = () => {
  if (isReviewMode.value) {
    router.push('/review')
  } else {
    router.back()
  }
}

const loadRawEmail = async () => {
  try {
    loadingRawEmail.value = true
    // Fetch both raw and cleaned email in parallel
    const [rawResponse, cleanedResponse] = await Promise.all([
      transactionsAPI.get(transaction.value.id, { include_raw: true }),
      transactionsAPI.getCleanedEmail(transaction.value.id)
    ])
    rawEmailBody.value = rawResponse.data.transaction.raw_email_body || '(no email body stored)'
    cleanedEmailBody.value = cleanedResponse.data.cleaned_body || '(no cleaned body)'
    cleanedEmailStats.value = {
      original_length: cleanedResponse.data.original_length || 0,
      cleaned_length: cleanedResponse.data.cleaned_length || 0
    }
    rawEmailLoaded.value = true
  } catch (error) {
    console.error('Failed to load raw email:', error)
    rawEmailBody.value = `Error loading email: ${error.message}`
    cleanedEmailBody.value = `Error loading cleaned email: ${error.message}`
  } finally {
    loadingRawEmail.value = false
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return null
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const debugReparse = async () => {
  if (!confirm('Re-parse this email with the latest parsing logic? Existing items will be replaced.')) return

  try {
    reparsing.value = true
    const result = await transactionsStore.reparseTransaction(transaction.value.id)
    // Reload the cleaned email view so the user can see updated results
    if (rawEmailLoaded.value) {
      const cleanedResponse = await transactionsAPI.getCleanedEmail(transaction.value.id)
      cleanedEmailBody.value = cleanedResponse.data.cleaned_body || '(no cleaned body)'
      cleanedEmailStats.value = {
        original_length: cleanedResponse.data.original_length || 0,
        cleaned_length: cleanedResponse.data.cleaned_length || 0
      }
    }
    alert(result.message || 'Email re-parsed successfully.')
  } catch (error) {
    alert('Failed to re-parse: ' + (error.response?.data?.error || error.message))
  } finally {
    reparsing.value = false
  }
}

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '$0.00'
  return `$${Number(amount).toFixed(2)}`
}

const formatItemAmount = (amount, quantity) => {
  if (amount === null || amount === undefined) return '$0.00'
  const total = Number(amount) * (quantity || 1)
  return `$${total.toFixed(2)}`
}
</script>

<style scoped>
.transaction-detail {
  max-width: 900px;
  margin: 0 auto;
}

.loading,
.error-state {
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
}

.header {
  margin-bottom: 2rem;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.review-badge {
  background: #e67e22;
  color: white;
  padding: 0.35rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
}

.btn-back {
  background: none;
  border: none;
  color: #3498db;
  font-size: 1rem;
  cursor: pointer;
}

.btn-back:hover {
  text-decoration: underline;
}

h1 {
  color: #2c3e50;
  margin: 0;
}

.transaction-info {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item label {
  display: block;
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.info-item span {
  font-weight: 600;
  color: #2c3e50;
}

.amount {
  color: #27ae60;
  font-size: 1.2rem;
}

.owner-name {
  color: #3498db;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  text-transform: capitalize;
}

.status-pending { background-color: #ecf0f1; color: #7f8c8d; }
.status-itemized { background-color: #fff3cd; color: #856404; }
.status-reviewed { background-color: #e8daef; color: #6c3483; }
.status-confirmed { background-color: #d4edda; color: #155724; }
.status-synced { background-color: #d1ecf1; color: #0c5460; }
.match-pending { background-color: #fff3cd; color: #856404; }
.match-auto_matched { background-color: #d4edda; color: #155724; }
.match-manually_matched { background-color: #d1ecf1; color: #0c5460; }
.match-no_match { background-color: #f8d7da; color: #721c24; }

.items-section {
  margin-bottom: 2rem;
}

.items-section h2 {
  color: #2c3e50;
  margin-bottom: 1.5rem;
}

/* Monarch Sync Summary */
.sync-summary {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.sync-summary h2 {
  color: #2e7d32;
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
}

.sync-summary-description {
  color: #558b2f;
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
}

.sync-loading {
  color: #7f8c8d;
  font-style: italic;
  padding: 1rem 0;
}

.category-breakdown {
  background: white;
  border-radius: 6px;
  overflow: hidden;
}

.category-row {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e8f5e9;
}

.category-row:last-child {
  border-bottom: none;
}

.category-row.category-total {
  background: #f1f8e9;
  font-weight: 600;
}

.category-row .category-name {
  color: #2c3e50;
}

.category-row .category-amount {
  color: #27ae60;
  font-weight: 500;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.item-card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 0.5rem;
}

.item-header h3 {
  color: #2c3e50;
  margin: 0;
  flex: 1;
}

.item-header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.item-amount {
  font-size: 1.2rem;
  font-weight: bold;
  color: #27ae60;
  margin-left: 1rem;
}

.btn-delete-item {
  background: none;
  border: 1px solid #e74c3c;
  color: #e74c3c;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;
  transition: all 0.2s;
}

.btn-delete-item:hover {
  background-color: #e74c3c;
  color: white;
}

.editable-amount {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.amount-input {
  width: 80px;
  padding: 0.25rem 0.4rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #2c3e50;
}

.amount-input:focus {
  outline: none;
  border-color: #3498db;
}

.item-description {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin: 0.5rem 0;
}

.item-details {
  display: flex;
  gap: 1rem;
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.category-section {
  border-top: 1px solid #ecf0f1;
  padding-top: 1rem;
  margin-top: 1rem;
}

.suggested-category {
  background-color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.suggested-category .label {
  color: #7f8c8d;
  margin-right: 0.5rem;
}

.suggested-category .category-name {
  font-weight: 600;
  color: #2c3e50;
}

.suggested-category .confidence {
  color: #27ae60;
  margin-left: 0.5rem;
}

.category-selector {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.category-selector label {
  font-weight: 600;
  color: #2c3e50;
}

.category-select {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.approval-status {
  margin-top: 0.5rem;
  color: #27ae60;
  font-size: 0.9rem;
}

.actions {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-accept {
  background-color: #8e44ad;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-accept:hover:not(:disabled) {
  background-color: #7d3c98;
}

.btn-accept:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

.btn-confirm {
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-confirm:hover:not(:disabled) {
  background-color: #229954;
}

.btn-confirm:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

.btn-reset {
  background-color: #e67e22;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-reset:hover:not(:disabled) {
  background-color: #d35400;
}

.btn-reset:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

.zero-order-notice {
  text-align: center;
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
}

.zero-order-notice p {
  color: #7f8c8d;
  margin-bottom: 1rem;
  font-size: 0.95rem;
}

.btn-dismiss {
  background-color: #95a5a6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-dismiss:hover:not(:disabled) {
  background-color: #7f8c8d;
}

.btn-dismiss:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-dismiss-transaction {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-dismiss-transaction:hover:not(:disabled) {
  background-color: #c0392b;
}

.btn-dismiss-transaction:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

.dismissed-notice {
  text-align: center;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  color: #7f8c8d;
  font-style: italic;
}

.dismissed-notice p {
  margin: 0 0 0.75rem 0;
}

.dismissed-notice .btn-restore {
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dismissed-notice .btn-restore:hover:not(:disabled) {
  background-color: #219a52;
}

.dismissed-notice .btn-restore:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-reparse {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-reparse:hover:not(:disabled) {
  background-color: #2980b9;
}

.btn-reparse:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

.status-dismissed {
  background-color: #e0e0e0;
  color: #7f8c8d;
}

.warning {
  color: #e67e22;
  margin-top: 1rem;
  font-size: 0.9rem;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  color: #7f8c8d;
}

/* Debug Section */
.debug-section {
  margin-top: 2rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #bdc3c7;
}

.btn-toggle-debug {
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: #7f8c8d;
  font-size: 0.9rem;
  cursor: pointer;
  text-align: left;
  font-family: monospace;
}

.btn-toggle-debug:hover {
  background-color: #ecf0f1;
}

.debug-content {
  padding: 1rem;
  border-top: 1px dashed #bdc3c7;
}

.debug-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  font-family: monospace;
}

.debug-row label {
  font-weight: 600;
  color: #7f8c8d;
  min-width: 180px;
  flex-shrink: 0;
}

.debug-row span {
  color: #2c3e50;
  word-break: break-all;
}

.debug-row .error-text {
  color: #e74c3c;
}

.debug-section-title {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin: 1rem 0 0.5rem 0;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #ecf0f1;
}

.debug-section-title:first-child {
  margin-top: 0;
}

.debug-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.cleaning-info {
  font-size: 0.75rem;
  color: #27ae60;
  margin: 0 0 0.5rem 0;
  font-style: italic;
}

.debug-tab {
  padding: 0.35rem 0.75rem;
  background: #ecf0f1;
  color: #7f8c8d;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  font-family: monospace;
}

.debug-tab:hover {
  background: #d5dbdb;
}

.debug-tab.active {
  background: #3498db;
  color: white;
}

.btn-load-email {
  padding: 0.5rem 1rem;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
}

.btn-load-email:hover:not(:disabled) {
  background: #2980b9;
}

.btn-load-email:disabled {
  background: #95a5a6;
  cursor: not-allowed;
}

.debug-email-body {
  margin-top: 1rem;
}

.debug-email-body label {
  display: block;
  font-weight: 600;
  color: #7f8c8d;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
}

.email-preview {
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 1rem;
  max-height: 400px;
  overflow: auto;
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #2c3e50;
}

.btn-debug-reparse {
  margin-top: 0.75rem;
  padding: 0.5rem 1rem;
  background: #e67e22;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  transition: background-color 0.2s;
}

.btn-debug-reparse:hover:not(:disabled) {
  background: #d35400;
}

.btn-debug-reparse:disabled {
  background: #95a5a6;
  cursor: not-allowed;
}
</style>
